import { useMemo, useState } from "react";
import { FiArrowUpRight, FiChevronLeft, FiExternalLink } from "react-icons/fi";
import { useNavigate, useParams } from "react-router";

import { tenants, type Tenant } from "@/app/tenants/utils";
import { Badge, Button, type BadgeTone } from "@/core/ui/atoms";
import { Banner, PageHeader, SummaryRow } from "@/core/ui/molecules";

type TenantDetailTab = "overview" | "configuration" | "users" | "billing";
type TenantConfigSubtab =
  | "product"
  | "modules"
  | "flags"
  | "forms"
  | "summary"
  | "customer"
  | "models"
  | "media"
  | "projects";

type TenantActivity = {
  readonly action: string;
  readonly actor: string;
  readonly date: string;
  readonly detail: string;
  readonly initials: string;
  readonly tone: "critical" | "cta" | "info" | "success";
};

type InstalledTemplatePack = {
  readonly dependencies: readonly string[];
  readonly description: string;
  readonly id: string;
  readonly itemTypes: readonly string[];
  readonly itemCount: number;
  readonly name: string;
  readonly version: string;
  readonly vertical: string;
};

type TenantUser = {
  readonly createdAt: string;
  readonly email: string;
  readonly initials: string;
  readonly lastLogin: string;
  readonly name: string;
  readonly role: string;
  readonly status: "active" | "invited" | "suspended";
};

type BillingEntry = {
  readonly amount: string;
  readonly date: string;
  readonly id: string;
  readonly label: string;
  readonly status: "failed" | "paid" | "pending";
};

type ConfigToggle = {
  readonly description: string;
  readonly isOverridden?: boolean;
  readonly label: string;
  readonly planDefault: boolean;
  readonly value: boolean;
};

const tabLabels = [
  ["overview", "Overview"],
  ["configuration", "Configuration"],
  ["users", "Users"],
  ["billing", "Billing"],
] as const satisfies readonly [TenantDetailTab, string][];

const configSubtabs = [
  ["product", "A - Product", false],
  ["modules", "B - Modules", false],
  ["flags", "C - Feature Flags", false],
  ["forms", "F - Forms", false],
  ["summary", "I - Summary", false],
  ["customer", "D - Customer", true],
  ["models", "E - Models", true],
  ["media", "G - Media", true],
  ["projects", "H - Projects", true],
] as const satisfies readonly [TenantConfigSubtab, string, boolean][];

const planBadgeTone = {
  Enterprise: "global",
  Go: "neutral",
  Growth: "recommended",
  Scale: "info",
} as const satisfies Record<Tenant["plan"], BadgeTone>;

const statusConfig = {
  active: { label: "Active", tone: "success" },
  exempt: { label: "Exempt", tone: "neutral" },
  suspended: { label: "Suspended", tone: "critical" },
  trial: { label: "Trial", tone: "warning" },
} as const satisfies Record<
  Tenant["status"],
  { readonly label: string; readonly tone: BadgeTone }
>;

const activityToneClasses = {
  critical: "bg-red-50 text-critical",
  cta: "bg-row-warning text-cta",
  info: "bg-blue-50 text-info",
  success: "bg-emerald-50 text-success",
} as const satisfies Record<TenantActivity["tone"], string>;

const packLibrary: Record<string, readonly InstalledTemplatePack[]> = {
  "ac-pro": [
    {
      dependencies: [
        "customer_base",
        "customer_hvac_extension",
        "forms",
        "media",
      ],
      description:
        "Core onboarding for HVAC tenants with customer, equipment, and service record defaults.",
      id: "hvac_starter_pack",
      itemCount: 6,
      itemTypes: ["form", "form", "model", "model", "media", "form"],
      name: "HVAC Starter Pack",
      version: "v2",
      vertical: "HVAC",
    },
    {
      dependencies: ["maintenance", "forms"],
      description:
        "Preventive maintenance forms and seasonal visit templates for recurring contracts.",
      id: "hvac_maintenance_pack",
      itemCount: 4,
      itemTypes: ["form", "form", "project", "media"],
      name: "Preventive Maintenance Pack",
      version: "v1",
      vertical: "HVAC",
    },
  ],
  default: [
    {
      dependencies: ["customer_base", "forms"],
      description:
        "Default tenant starter pack created from the prototype flow for plan-aligned setup.",
      id: "core_default_pack",
      itemCount: 3,
      itemTypes: ["form", "model", "media"],
      name: "Core Starter Pack",
      version: "v1",
      vertical: "Generic",
    },
  ],
  termistop: [
    {
      dependencies: ["customer_base", "customer_pest_extension", "forms"],
      description:
        "Inspection, treatment, and follow-up templates used during Pest Control onboarding.",
      id: "pest_control_pack",
      itemCount: 5,
      itemTypes: ["form", "form", "form", "model", "media"],
      name: "Pest Control Core Pack",
      version: "v2",
      vertical: "Pest Control",
    },
  ],
};

const activityLog: Record<string, readonly TenantActivity[]> = {
  "ac-pro": [
    {
      action: "published Growth Plan v3",
      actor: "Ethan Reyes",
      date: "3d ago",
      detail:
        "Q2 commercial repositioning applied to this tenant with Forms Library enabled.",
      initials: "ER",
      tone: "cta",
    },
    {
      action: "signed in as owner@acpro.com",
      actor: "Marcos Jimenez",
      date: "5d ago",
      detail: "Customer support ticket #4821 for scheduler permissions review.",
      initials: "MJ",
      tone: "info",
    },
    {
      action: "created tenant",
      actor: "Ethan Reyes",
      date: "45d ago",
      detail: "Plan: Growth · Vertical: HVAC · Country: CR · Stripe linked.",
      initials: "ER",
      tone: "success",
    },
  ],
  default: [
    {
      action: "reviewed tenant configuration",
      actor: "Olivia Sanchez",
      date: "2d ago",
      detail:
        "Prototype-aligned configuration confirmed for the detail screen.",
      initials: "OS",
      tone: "info",
    },
  ],
  termistop: [
    {
      action: "extended trial",
      actor: "Olivia Sanchez",
      date: "2d ago",
      detail: "+14 days due to pending Stripe activation.",
      initials: "OS",
      tone: "cta",
    },
  ],
};

const billingHistory: Record<string, readonly BillingEntry[]> = {
  "ac-pro": [
    {
      amount: "$159.00",
      date: "May 12, 2026",
      id: "INV-3402",
      label: "Growth v3 monthly",
      status: "paid",
    },
    {
      amount: "$159.00",
      date: "Apr 12, 2026",
      id: "INV-3276",
      label: "Growth v3 monthly",
      status: "paid",
    },
    {
      amount: "$159.00",
      date: "Mar 12, 2026",
      id: "INV-3120",
      label: "Growth v3 monthly",
      status: "paid",
    },
  ],
  default: [
    {
      amount: "$49.00",
      date: "May 01, 2026",
      id: "INV-3011",
      label: "Recurring subscription",
      status: "paid",
    },
    {
      amount: "$49.00",
      date: "Apr 01, 2026",
      id: "INV-2870",
      label: "Recurring subscription",
      status: "paid",
    },
  ],
  termistop: [
    {
      amount: "$0.00",
      date: "May 20, 2026",
      id: "TRIAL-EXT",
      label: "Trial extension",
      status: "pending",
    },
    {
      amount: "$49.00",
      date: "May 06, 2026",
      id: "INV-3324",
      label: "Stripe activation attempt",
      status: "failed",
    },
  ],
};

export default function TenantDetailsPage() {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [activeTab, setActiveTab] = useState<TenantDetailTab>("overview");
  const [activeConfigTab, setActiveConfigTab] =
    useState<TenantConfigSubtab>("product");

  const tenant = useMemo(
    () => tenants.find((entry) => entry.id === tenantId) ?? null,
    [tenantId],
  );

  if (!tenant) {
    return (
      <section className="rounded-panel border border-border bg-surface p-6 shadow-panel">
        <h1 className="text-xl font-semibold text-ink">Tenant not found</h1>
        <p className="mt-2 text-sm text-ink-muted">
          The requested tenant record is not available in the current mock data.
        </p>
        <div className="mt-4">
          <Button onClick={() => navigate(-1)}>
            <FiChevronLeft aria-hidden="true" />
            Back
          </Button>
        </div>
      </section>
    );
  }

  const users = getTenantUsers(tenant);

  return (
    <>
      <PageHeader
        actions={
          <>
            <Button onClick={() => openClientApp(tenant)}>
              <FiExternalLink aria-hidden="true" />
              Open client app
            </Button>
            <Button variant="primary">
              <FiArrowUpRight aria-hidden="true" />
              Actions
            </Button>
          </>
        }
        breadcrumb={["Super Admin", "Tenants", tenant.name]}
        subtitle={`${tenant.vertical} · ${tenant.country} · ${tenant.product} workspace`}
        title={<TenantTitle tenant={tenant} />}
      />

      <div className="mb-5 flex flex-wrap gap-2 border-b border-border">
        {tabLabels.map(([tabId, label]) => (
          <button
            className={`border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
              activeTab === tabId
                ? "border-cta text-cta"
                : "border-transparent text-ink-subtle hover:text-ink"
            }`}
            key={tabId}
            onClick={() => setActiveTab(tabId)}
            type="button"
          >
            {label}
            {tabId === "configuration" && hasOverrides(tenant) ? (
              <span className="ml-2 inline-flex rounded border border-warning bg-row-warning px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-warning">
                override
              </span>
            ) : null}
            {tabId === "users" ? (
              <span className="ml-1 text-[11px] text-ink-subtle">
                {users.length}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {activeTab === "overview" ? <TenantOverview tenant={tenant} /> : null}
      {activeTab === "configuration" ? (
        <TenantConfiguration
          activeConfigTab={activeConfigTab}
          onChangeTab={setActiveConfigTab}
          tenant={tenant}
        />
      ) : null}
      {activeTab === "users" ? (
        <TenantUsers tenant={tenant} users={users} />
      ) : null}
      {activeTab === "billing" ? <TenantBilling tenant={tenant} /> : null}
    </>
  );
}

function TenantTitle({ tenant }: { readonly tenant: Tenant }) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span>{tenant.name}</span>
      <Badge tone={planBadgeTone[tenant.plan]}>
        {tenant.plan} v{tenant.planVersion}
      </Badge>
      <Badge dot tone={statusConfig[tenant.status].tone}>
        {tenant.status === "trial" && tenant.trialDays
          ? `Trial · ${tenant.trialDays}d`
          : statusConfig[tenant.status].label}
      </Badge>
      <span className="text-xs font-medium text-ink-subtle">
        {tenant.vertical} · {tenant.countryCode}
      </span>
    </span>
  );
}

function TenantOverview({ tenant }: { readonly tenant: Tenant }) {
  const installedPacks = packLibrary[tenant.id] ?? packLibrary.default;
  const recentActivity = activityLog[tenant.id] ?? activityLog.default;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <OverviewMetric
          label="Plan"
          sublabel={`v${tenant.planVersion}`}
          value={tenant.plan}
        />
        <OverviewMetric
          label="Users"
          sublabel="Active accounts"
          value={tenant.users}
        />
        <OverviewMetric
          label="Country"
          sublabel={tenant.country}
          value={tenant.countryCode}
        />
        <OverviewMetric
          label="Created"
          sublabel={`by ${tenant.createdBy ?? "-"}`}
          value={tenant.createdAt}
        />
      </div>

      {tenant.status === "suspended" ? (
        <Banner tone="error">
          <strong>This tenant is suspended.</strong>{" "}
          {tenant.suspendedReason ??
            "Access is currently blocked for all users."}
        </Banner>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
          <h2 className="mb-3 text-sm font-semibold text-ink">
            Tenant Information
          </h2>
          <SummaryRow
            label="Tenant ID"
            value={<code className="font-mono text-xs">{tenant.id}</code>}
          />
          <SummaryRow label="Product" value={tenant.product} />
          <SummaryRow label="Vertical" value={tenant.vertical} />
          <SummaryRow
            label="Owner"
            value={
              <span className="text-right">
                <span className="block">{tenant.ownerName}</span>
                <span className="text-xs font-normal text-ink-subtle">
                  {tenant.owner}
                </span>
              </span>
            }
          />
          <SummaryRow
            label="Billing"
            value={
              <span className="text-right">
                <span className="block">{tenant.billing}</span>
                {tenant.stripeId ? (
                  <span className="font-mono text-[11px] font-normal text-ink-subtle">
                    {tenant.stripeId}
                  </span>
                ) : null}
              </span>
            }
          />
          <SummaryRow label="Last login" value={tenant.lastLogin} />
        </section>

        <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
          <h2 className="mb-3 text-sm font-semibold text-ink">
            Recent Activity
          </h2>
          <div className="space-y-3">
            {recentActivity.map((entry) => (
              <div
                className="flex items-start gap-3 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0"
                key={`${entry.actor}-${entry.action}-${entry.date}`}
              >
                <span
                  className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${activityToneClasses[entry.tone]}`}
                >
                  {entry.initials}
                </span>
                <div className="min-w-0 flex-1 text-[13px] leading-5">
                  <div className="font-medium text-ink">
                    {entry.actor} {entry.action}
                  </div>
                  <div className="text-ink-muted">{entry.detail}</div>
                </div>
                <div className="text-right text-[11px] text-ink-subtle">
                  {entry.date}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-ink">
              Installed Template Packs
            </h2>
            <p className="mt-1 text-xs text-ink-subtle">
              Installed atomically at tenant creation and sourced from Library.
            </p>
          </div>
          <Badge tone="success">{installedPacks.length} installed</Badge>
        </div>
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          {installedPacks.map((pack) => (
            <article
              className="rounded-panel border border-border bg-panel-muted p-4"
              key={pack.id}
            >
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-[13px] font-semibold text-ink">
                  {pack.name}
                </h3>
                <Badge className="font-mono" tone="neutral">
                  {pack.version}
                </Badge>
                <Badge tone="critical">{pack.vertical}</Badge>
              </div>
              <p className="mt-2 text-xs leading-5 text-ink-muted">
                {pack.description}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {pack.itemTypes.slice(0, 4).map((itemType, index) => (
                  <span
                    className="rounded border border-border bg-surface px-2 py-0.5 font-mono text-[10px] text-ink-muted"
                    key={`${pack.id}-${itemType}-${index}`}
                  >
                    {itemType}
                  </span>
                ))}
                {pack.itemCount > 4 ? (
                  <span className="text-[11px] text-ink-subtle">
                    +{pack.itemCount - 4} more
                  </span>
                ) : null}
              </div>
              <div className="mt-3 rounded-control border border-border bg-surface p-3">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-subtle">
                  Installation order
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {pack.dependencies.map((dependency, index) => (
                    <span
                      className="inline-flex items-center gap-1.5 rounded border border-border bg-panel-muted px-2 py-1 text-[10px] text-ink-muted"
                      key={`${pack.id}-${dependency}`}
                    >
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-info text-white">
                        {index + 1}
                      </span>
                      <span className="font-mono">{dependency}</span>
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
        <Banner tone="info">
          <strong>Library</strong> creates content, <strong>Super Admin</strong>{" "}
          controls availability, and <strong>Tenant Setup</strong> installs it
          atomically.
        </Banner>
      </section>
    </div>
  );
}

function TenantConfiguration({
  activeConfigTab,
  onChangeTab,
  tenant,
}: {
  readonly activeConfigTab: TenantConfigSubtab;
  readonly onChangeTab: (tab: TenantConfigSubtab) => void;
  readonly tenant: Tenant;
}) {
  const moduleConfig = getModuleConfig(tenant);
  const flagConfig = getFeatureFlagConfig(tenant);
  const formConfig = getFormConfiguration(tenant);
  const overrideCount = moduleConfig.filter(
    (entry) => entry.isOverridden,
  ).length;

  return (
    <div className="space-y-4">
      {overrideCount > 0 ? (
        <Banner tone="warning">
          <strong>
            {overrideCount} override{overrideCount > 1 ? "s" : ""} active on
            this tenant.
          </strong>{" "}
          These differ from the {tenant.plan} defaults and should stay
          exceptional.
        </Banner>
      ) : null}

      <div className="flex flex-wrap gap-2 rounded-panel bg-gray-100 p-1">
        {configSubtabs.map(([subtabId, label, isV15]) => (
          <button
            className={`rounded-control px-3 py-2 text-xs font-medium transition-colors ${
              activeConfigTab === subtabId
                ? "bg-surface text-cta shadow-panel"
                : "text-ink-muted hover:text-ink"
            }`}
            key={subtabId}
            onClick={() => onChangeTab(subtabId)}
            type="button"
          >
            {label}
            {isV15 ? (
              <span className="ml-2 rounded border border-yellow-300 bg-row-warning px-1 py-0.5 text-[9px] font-semibold uppercase tracking-[0.04em] text-warning">
                V1.5
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {activeConfigTab === "product" ? (
        <ProductConfiguration tenant={tenant} />
      ) : null}
      {activeConfigTab === "modules" ? (
        <ToggleGridCard title="B - Modules" toggles={moduleConfig} />
      ) : null}
      {activeConfigTab === "flags" ? (
        <ToggleGridCard title="C - Feature Flags" toggles={flagConfig} />
      ) : null}
      {activeConfigTab === "forms" ? (
        <FormsConfiguration forms={formConfig} />
      ) : null}
      {activeConfigTab === "summary" ? (
        <ConfigurationSummary tenant={tenant} />
      ) : null}
      {activeConfigTab === "customer" ? (
        <V15Card title="Customer Visibility" />
      ) : null}
      {activeConfigTab === "models" ? (
        <V15Card title="Model Templates" />
      ) : null}
      {activeConfigTab === "media" ? <V15Card title="Media Templates" /> : null}
      {activeConfigTab === "projects" ? (
        <V15Card title="Project Templates" />
      ) : null}

      <div className="flex justify-end gap-2">
        <Button>Discard</Button>
        <Button variant="primary">Review and Save</Button>
      </div>
    </div>
  );
}

function ProductConfiguration({ tenant }: { readonly tenant: Tenant }) {
  const clientLink = `https://app.${tenant.product === "Enterprise" ? "sitehubpro" : "getsitehub"}.com/${tenant.id}`;

  return (
    <div className="space-y-4">
      <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
        <h2 className="mb-3 text-sm font-semibold text-ink">
          Product Configuration
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ConfigField
            label="Product"
            value={tenant.product}
            helpText="SaaS = getsitehub.com · Enterprise = sitehubpro.com"
          />
          <ConfigField
            label="Plan"
            value={tenant.plan}
            helpText="Defines defaults for configuration sub-sections."
          />
          <ConfigField
            label="Vertical"
            value={tenant.vertical}
            helpText="Vertical organizes and prioritizes. It does not restrict installation."
          />
          <ConfigField
            label="Domain"
            value={
              tenant.product === "Enterprise"
                ? "sitehubpro.com"
                : "getsitehub.com"
            }
            helpText="Auto-set from Product."
          />
        </div>
        <div className="mt-4">
          <div className="mb-1 text-xs font-medium text-ink-muted">
            Client Link
          </div>
          <div className="flex flex-col gap-2 md:flex-row">
            <div className="flex-1 rounded-control border border-border bg-panel-muted px-3 py-2 font-mono text-xs text-ink-muted">
              {clientLink}
            </div>
            <Button onClick={() => openClientApp(tenant)}>Open</Button>
          </div>
        </div>
      </section>

      <Banner tone="warning">
        <strong>Changing plan recalculates defaults.</strong> Modules, flags,
        forms, and advanced configuration areas should be reviewed before
        saving.
      </Banner>
    </div>
  );
}

function ToggleGridCard({
  title,
  toggles,
}: {
  readonly title: string;
  readonly toggles: readonly ConfigToggle[];
}) {
  return (
    <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
      <h2 className="mb-3 text-sm font-semibold text-ink">{title}</h2>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {toggles.map((toggle) => (
          <div
            className={`rounded-control border px-3 py-3 ${toggle.isOverridden ? "border-warning bg-row-warning" : "border-border bg-panel-muted"}`}
            key={toggle.label}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-semibold text-ink">
                  {toggle.label}
                </div>
                <div className="mt-1 text-xs leading-5 text-ink-muted">
                  {toggle.description}
                </div>
              </div>
              <Badge tone={toggle.value ? "success" : "disabled"}>
                {toggle.value ? "On" : "Off"}
              </Badge>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-ink-subtle">
              <span>Plan default: {toggle.planDefault ? "On" : "Off"}</span>
              {toggle.isOverridden ? (
                <Badge tone="warning">Override</Badge>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FormsConfiguration({
  forms,
}: {
  readonly forms: readonly { readonly label: string; readonly value: string }[];
}) {
  return (
    <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
      <h2 className="mb-3 text-sm font-semibold text-ink">F - Forms</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {forms.map((entry) => (
          <div
            className="rounded-control border border-border bg-panel-muted px-3 py-3"
            key={entry.label}
          >
            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-subtle">
              {entry.label}
            </div>
            <div className="mt-1 text-xs font-medium text-ink">
              {entry.value}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ConfigurationSummary({ tenant }: { readonly tenant: Tenant }) {
  const moduleConfig = getModuleConfig(tenant);
  const flagConfig = getFeatureFlagConfig(tenant);

  return (
    <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
      <h2 className="mb-3 text-sm font-semibold text-ink">I - Summary</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-ink-subtle">
            Product
          </div>
          <SummaryRow label="Product" value={tenant.product} />
          <SummaryRow
            label="Plan"
            value={`${tenant.plan} v${tenant.planVersion}`}
          />
          <SummaryRow label="Vertical" value={tenant.vertical} />
        </div>
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-ink-subtle">
            Overrides
          </div>
          <SummaryRow
            label="Module overrides"
            value={moduleConfig.filter((entry) => entry.isOverridden).length}
          />
          <SummaryRow
            label="Flag overrides"
            value={flagConfig.filter((entry) => entry.isOverridden).length}
          />
          <SummaryRow label="Billing" value={tenant.billing} />
        </div>
      </div>
    </section>
  );
}

function V15Card({ title }: { readonly title: string }) {
  return (
    <div className="space-y-4">
      <Banner tone="info">
        <strong>Advanced configuration - V1.5 candidate.</strong> This
        sub-section is shown from the prototype for design parity, but remains
        informational in the current admin app.
      </Banner>
      <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-ink-muted">
          The demo reserves this space for deeper tenant-level controls after
          the initial launch. The page now reflects that structure without
          enabling edits yet.
        </p>
      </section>
    </div>
  );
}

function TenantUsers({
  tenant,
  users,
}: {
  readonly tenant: Tenant;
  readonly users: readonly TenantUser[];
}) {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
      <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-ink">Users</h2>
            <p className="mt-1 text-xs text-ink-subtle">
              Owner and operator accounts for {tenant.name}.
            </p>
          </div>
          <Button variant="primary">Invite user</Button>
        </div>
        <div className="space-y-3">
          {users.map((user) => (
            <article
              className="flex flex-col gap-3 rounded-panel border border-border bg-panel-muted p-3 md:flex-row md:items-center md:justify-between"
              key={user.email}
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-sidebar text-xs font-semibold text-white">
                  {user.initials}
                </span>
                <div>
                  <div className="text-sm font-semibold text-ink">
                    {user.name}
                  </div>
                  <div className="text-xs text-ink-muted">{user.email}</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge
                  tone={
                    user.status === "active"
                      ? "success"
                      : user.status === "invited"
                        ? "warning"
                        : "critical"
                  }
                >
                  {user.status}
                </Badge>
                <span className="text-ink-muted">{user.role}</span>
                <span className="text-ink-subtle">
                  Last login: {user.lastLogin}
                </span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <aside className="space-y-4">
        <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
          <h2 className="mb-3 text-sm font-semibold text-ink">
            Access Summary
          </h2>
          <SummaryRow label="Total users" value={users.length} />
          <SummaryRow label="Owner" value={tenant.ownerName} />
          <SummaryRow label="Last sign in" value={tenant.lastLogin} />
        </section>
      </aside>
    </div>
  );
}

function TenantBilling({ tenant }: { readonly tenant: Tenant }) {
  const invoices = billingHistory[tenant.id] ?? billingHistory.default;
  const billingStatusTone: BadgeTone =
    tenant.status === "suspended"
      ? "critical"
      : tenant.status === "trial"
        ? "warning"
        : "success";

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
      <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-ink">Billing</h2>
            <p className="mt-1 text-xs text-ink-subtle">
              Prototype-aligned billing summary and invoice history.
            </p>
          </div>
          <Badge tone={billingStatusTone}>{tenant.billing}</Badge>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <OverviewMetric
            label="Plan price"
            sublabel="Current tier"
            value={getPlanPriceLabel(tenant.plan)}
          />
          <OverviewMetric
            label="Payment rail"
            sublabel="Charge method"
            value={tenant.billing}
          />
          <OverviewMetric
            label="Stripe ID"
            sublabel="Linked customer"
            value={tenant.stripeId ?? "Not linked"}
          />
        </div>

        <div className="mt-4 overflow-hidden rounded-panel border border-border">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className="bg-panel-muted text-[10px] uppercase tracking-[0.06em] text-ink-subtle">
              <tr>
                <th className="px-4 py-3 font-semibold">Invoice</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Amount</th>
                <th className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((entry) => (
                <tr className="border-t border-border" key={entry.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink">{entry.id}</div>
                    <div className="text-xs text-ink-subtle">{entry.label}</div>
                  </td>
                  <td className="px-4 py-3 text-ink-muted">{entry.date}</td>
                  <td className="px-4 py-3 font-medium text-ink">
                    {entry.amount}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      tone={
                        entry.status === "paid"
                          ? "success"
                          : entry.status === "pending"
                            ? "warning"
                            : "critical"
                      }
                    >
                      {entry.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <aside className="space-y-4">
        <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
          <h2 className="mb-3 text-sm font-semibold text-ink">
            Billing Summary
          </h2>
          <SummaryRow
            label="Status"
            value={statusConfig[tenant.status].label}
          />
          <SummaryRow label="Owner" value={tenant.owner} />
          <SummaryRow label="Country" value={tenant.country} />
        </section>
      </aside>
    </div>
  );
}

function OverviewMetric({
  label,
  sublabel,
  value,
}: {
  readonly label: string;
  readonly sublabel: string;
  readonly value: number | string;
}) {
  return (
    <div className="rounded-panel border border-border bg-surface px-4 py-3 shadow-panel">
      <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-subtle">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-ink">{value}</div>
      <div className="text-xs text-ink-subtle">{sublabel}</div>
    </div>
  );
}

function ConfigField({
  helpText,
  label,
  value,
}: {
  readonly helpText: string;
  readonly label: string;
  readonly value: string;
}) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-ink-muted">{label}</div>
      <div className="rounded-control border border-border bg-panel-muted px-3 py-2 text-sm text-ink">
        {value}
      </div>
      <div className="mt-1 text-[11px] text-ink-subtle">{helpText}</div>
    </div>
  );
}

function openClientApp(tenant: Tenant) {
  const clientLink = `https://app.${tenant.product === "Enterprise" ? "sitehubpro" : "getsitehub"}.com/${tenant.id}`;
  window.open(clientLink, "_blank", "noopener,noreferrer");
}

function hasOverrides(tenant: Tenant) {
  return getModuleConfig(tenant).some((entry) => entry.isOverridden);
}

function getTenantUsers(tenant: Tenant): readonly TenantUser[] {
  const generatedUsers = Math.max(Math.min(tenant.users, 4), 2);

  return Array.from({ length: generatedUsers }, (_, index) => {
    if (index === 0) {
      return {
        createdAt: tenant.createdAt,
        email: tenant.owner,
        initials: getInitials(tenant.ownerName),
        lastLogin: tenant.lastLogin,
        name: tenant.ownerName,
        role: "Owner",
        status: "active",
      } as const;
    }

    const userName = `${tenant.vertical.split(" ")[0]} Operator ${index}`;
    const emailPrefix = userName.toLowerCase().replace(/\s+/g, ".");

    return {
      createdAt: tenant.createdAt,
      email: `${emailPrefix}@${tenant.code}.sitehub.test`,
      initials: getInitials(userName),
      lastLogin: index === 1 ? "1d ago" : index === 2 ? "3d ago" : "Invited",
      name: userName,
      role: index === 1 ? "Manager" : index === 2 ? "Dispatcher" : "Technician",
      status:
        index === generatedUsers - 1 && tenant.status === "trial"
          ? "invited"
          : "active",
    } as const;
  });
}

function getModuleConfig(tenant: Tenant): readonly ConfigToggle[] {
  const isScale = tenant.plan === "Scale" || tenant.plan === "Enterprise";
  const isEnterprise = tenant.plan === "Enterprise";
  const hasModuleOverride = tenant.id === "ac-pro";

  return [
    {
      description:
        "Core operational workspace for work orders and field execution.",
      label: "Jobs / Work Orders",
      planDefault: true,
      value: true,
    },
    {
      description: "Calendar and dispatch flows for technician scheduling.",
      label: "Schedule",
      planDefault: true,
      value: true,
    },
    {
      description: "Customer records and account hierarchy.",
      label: "Customers",
      planDefault: true,
      value: true,
    },
    {
      description: "Forms catalog and field execution reporting.",
      isOverridden: hasModuleOverride,
      label: "Forms",
      planDefault: tenant.plan !== "Go",
      value: tenant.plan !== "Go" || hasModuleOverride,
    },
    {
      description: "Inventory tracking and part usage.",
      label: "Inventory",
      planDefault: isScale,
      value: isScale,
    },
    {
      description: "Preventive maintenance programs and recurring jobs.",
      label: "Maintenance",
      planDefault: isScale,
      value: isScale,
    },
    {
      description: "Advanced asset lifecycle and enterprise controls.",
      label: "Assets Advanced",
      planDefault: isEnterprise,
      value: isEnterprise,
    },
  ] as const;
}

function getFeatureFlagConfig(tenant: Tenant): readonly ConfigToggle[] {
  const isScale = tenant.plan === "Scale" || tenant.plan === "Enterprise";
  const isEnterprise = tenant.plan === "Enterprise";

  return [
    {
      description: "Embedded job report enabled in field workflows.",
      label: "Embedded job report",
      planDefault: true,
      value: true,
    },
    {
      description: "Editable forms for branch-specific adjustments.",
      label: "Forms editable",
      planDefault: isScale,
      value: isScale,
    },
    {
      description: "Form builder access for advanced teams.",
      label: "Form builder",
      planDefault: isEnterprise,
      value: isEnterprise,
    },
    {
      description: "Assignable media templates sourced from Library.",
      label: "Media assignable",
      planDefault: isScale,
      value: isScale,
    },
    {
      description: "Model builder for enterprise data structure changes.",
      label: "Model builder",
      planDefault: isEnterprise,
      value: isEnterprise,
    },
  ] as const;
}

function getFormConfiguration(tenant: Tenant) {
  return [
    {
      label: "Default source",
      value: tenant.plan === "Go" ? "Starter forms" : "Library forms",
    },
    {
      label: "Editable",
      value:
        tenant.plan === "Scale" || tenant.plan === "Enterprise"
          ? "Enabled"
          : "Disabled",
    },
    { label: "Vertical focus", value: tenant.vertical },
  ] as const;
}

function getPlanPriceLabel(plan: Tenant["plan"]) {
  return {
    Enterprise: "Contact",
    Go: "$49 /mo",
    Growth: "$159 /mo",
    Scale: "$399 /mo",
  }[plan];
}

function getInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}
